import {InventoryItem, InventoryPropertiesEnum, ItemQuantity} from "./Inventory";
export {TECHNOLOGIES, TECHNOLOGY_SECTIONS, getTechnologyScienceWeight} from "./TechnologyData";
export {CRAFTING_RECIPES} from "./CraftingRecipes";
export type {CraftingRecipe} from "./CraftingRecipes";

export const TIME_INCREMENT_MS = 1000;

export type ManualActionDefinition = {
    id: string;
    resourceId: string;
    durationMs: number;
    reward: ItemQuantity;
};

export type MinerBehavior = {
    id: string;
    supportedResources: string[];
    cycleTimeMs: number;
    outputPerCycle: number;
    fuelCostPerCycle?: ItemQuantity;
};

export type SmelterRecipeDefinition = {
    id: string;
    inputId: string;
    outputId: string;
    inputQuantity: number;
    outputQuantity: number;
    cycleTimeMs: number;
    fuelCostPerCycle?: ItemQuantity;
};

const coal: InventoryItem = {
    id: "coal",
    name: "Coal",
    category: "resource",
    properties: [InventoryPropertiesEnum.fuel],
    buildCost: 10,
};

const wood: InventoryItem = {
    id: "wood",
    name: "Wood",
    category: "resource",
    properties: [InventoryPropertiesEnum.fuel],
    buildCost: 5,
};

const iron: InventoryItem = {
    id: "iron",
    name: "Iron Ore",
    category: "resource",
    properties: [InventoryPropertiesEnum.smeltable],
    buildCost: 10,
};

const copper: InventoryItem = {
    id: "copper",
    name: "Copper Ore",
    category: "resource",
    properties: [InventoryPropertiesEnum.smeltable],
    buildCost: 10,
};

const stone: InventoryItem = {
    id: "stone",
    name: "Stone",
    category: "resource",
    properties: [InventoryPropertiesEnum.smeltable],
    buildCost: 10,
};

const ironplate: InventoryItem = {
    id: "ironplate",
    name: "Iron Plate",
    category: "basicMaterial",
    properties: [InventoryPropertiesEnum.smeltable],
    buildCost: 10,
};

const copperplate: InventoryItem = {
    id: "copperplate",
    name: "Copper Plate",
    category: "basicMaterial",
    properties: [InventoryPropertiesEnum.smeltable],
    buildCost: 10,
};

const copperWire: InventoryItem = {
    id: "copperWire",
    name: "Copper Wire",
    category: "basicMaterial",
    properties: [],
};

const ironStick: InventoryItem = {
    id: "ironStick",
    name: "Iron Stick",
    category: "basicMaterial",
    properties: [],
};

const steelPlate: InventoryItem = {
    id: "steelPlate",
    name: "Steel Plate",
    category: "basicMaterial",
    properties: [InventoryPropertiesEnum.smeltable],
    buildCost: 25,
};

const stoneBrick: InventoryItem = {
    id: "stoneBrick",
    name: "Stone Brick",
    category: "basicMaterial",
    properties: [InventoryPropertiesEnum.smeltable],
    buildCost: 20,
};

const electronicCircuit: InventoryItem = {
    id: "electronicCircuit",
    name: "Electronic Circuit",
    category: "basicMaterial",
    properties: [],
};

const transportBelt: InventoryItem = {
    id: "transportBelt",
    name: "Transport Belt",
    category: "basicBuilding",
    properties: [InventoryPropertiesEnum.building],
};

const inserter: InventoryItem = {
    id: "inserter",
    name: "Inserter",
    category: "basicBuilding",
    properties: [InventoryPropertiesEnum.building],
};

const miner1: InventoryItem = {
    id: "miner1",
    name: "Burner Miner Drill",
    category: "basicBuilding",
    properties: [InventoryPropertiesEnum.miner, InventoryPropertiesEnum.building],
    buildPower: 10,
    buildCost: 20,
};

const miner2: InventoryItem = {
    id: "miner2",
    name: "Electric Miner Drill",
    category: "basicBuilding",
    properties: [InventoryPropertiesEnum.miner, InventoryPropertiesEnum.building],
    buildPower: 20,
    buildCost: 40,
    powerUse: 20,
};

const smelter1: InventoryItem = {
    id: "smelter1",
    name: "Stone Furnace",
    category: "basicBuilding",
    properties: [InventoryPropertiesEnum.smelter, InventoryPropertiesEnum.building],
    buildPower: 10,
    buildCost: 20,
};

const steelFurnace: InventoryItem = {
    id: "steelFurnace",
    name: "Steel Furnace",
    category: "basicBuilding",
    properties: [InventoryPropertiesEnum.smelter, InventoryPropertiesEnum.building],
    buildPower: 15,
    buildCost: 40,
};

const ironPipe: InventoryItem = {
    id: "ironPipe",
    name: "Iron Pipe",
    category: "basicMaterial",
    properties: [],
};

const waterPump: InventoryItem = {
    id: "waterPump",
    name: "Water Pump",
    category: "basicBuilding",
    properties: [InventoryPropertiesEnum.building],
};

const waterBoiler: InventoryItem = {
    id: "waterBoiler",
    name: "Water Boiler",
    category: "basicBuilding",
    properties: [InventoryPropertiesEnum.building],
};

const steamGenerator: InventoryItem = {
    id: "steamGenerator",
    name: "Basic Steam Power Generator",
    category: "basicBuilding",
    properties: [InventoryPropertiesEnum.building],
};

const ironGear: InventoryItem = {
    id: "ironGear",
    name: "Iron Gear",
    category: "basicMaterial",
    properties: [],
};

const circuitAssembler: InventoryItem = {
    id: "circuitAssembler",
    name: "Circuit Assembler",
    category: "basicBuilding",
    properties: [InventoryPropertiesEnum.building],
    buildPower: 25,
    buildCost: 45,
    powerUse: 20,
};

const scienceAssembler: InventoryItem = {
    id: "scienceAssembler",
    name: "Science Assembler",
    category: "scienceBuilding",
    properties: [InventoryPropertiesEnum.building],
    buildPower: 15,
    buildCost: 25,
};

const researchLab: InventoryItem = {
    id: "researchLab",
    name: "Research Lab",
    category: "scienceBuilding",
    properties: [InventoryPropertiesEnum.building],
    buildPower: 20,
    buildCost: 30,
};

const sciencePack1: InventoryItem = {
    id: "sciencePack1",
    name: "Basic Science Pack",
    category: "science",
    properties: [],
};

const sciencePack2: InventoryItem = {
    id: "sciencePack2",
    name: "Intermediate Science Pack",
    category: "science",
    properties: [],
};

const sciencePack3: InventoryItem = {
    id: "sciencePack3",
    name: "Advanced Science Pack",
    category: "science",
    properties: [],
};

const advancedCircuit: InventoryItem = {
    id: "advancedCircuit",
    name: "Advanced Circuit",
    category: "advancedMaterial",
    properties: [],
};

const battery: InventoryItem = {
    id: "battery",
    name: "Battery",
    category: "advancedMaterial",
    properties: [],
};

const processingUnit: InventoryItem = {
    id: "processingUnit",
    name: "Processing Unit",
    category: "advancedMaterial",
    properties: [],
};

const solarPanel: InventoryItem = {
    id: "solarPanel",
    name: "Solar Panel",
    category: "powerBuilding",
    properties: [InventoryPropertiesEnum.building],
    buildPower: 30,
    buildCost: 50,
};

const accumulator: InventoryItem = {
    id: "accumulator",
    name: "Accumulator",
    category: "powerBuilding",
    properties: [InventoryPropertiesEnum.building],
    buildPower: 20,
    buildCost: 40,
};

const fastAssembler: InventoryItem = {
    id: "fastAssembler",
    name: "Fast Circuit Assembler",
    category: "advancedBuilding",
    properties: [InventoryPropertiesEnum.building],
    buildPower: 35,
    buildCost: 60,
    powerUse: 30,
};

const electricFurnace: InventoryItem = {
    id: "electricFurnace",
    name: "Electric Furnace",
    category: "advancedBuilding",
    properties: [InventoryPropertiesEnum.smelter, InventoryPropertiesEnum.building],
    buildPower: 25,
    buildCost: 50,
    powerUse: 50,
};

const speedModule1: InventoryItem = {
    id: "speedModule1",
    name: "Speed Module 1",
    category: "module",
    properties: [InventoryPropertiesEnum.module],
};

const productivityModule1: InventoryItem = {
    id: "productivityModule1",
    name: "Productivity Module 1",
    category: "module",
    properties: [InventoryPropertiesEnum.module],
};

const efficiencyModule1: InventoryItem = {
    id: "efficiencyModule1",
    name: "Efficiency Module 1",
    category: "module",
    properties: [InventoryPropertiesEnum.module],
};

export const GameItems: Record<string, InventoryItem> = {
    coal,
    wood,
    iron,
    copper,
    stone,
    ironplate,
    copperplate,
    copperWire,
    ironStick,
    steelPlate,
    stoneBrick,
    electronicCircuit,
    transportBelt,
    inserter,
    miner1,
    miner2,
    smelter1,
    steelFurnace,
    electricFurnace,
    circuitAssembler,
    fastAssembler,
    scienceAssembler,
    researchLab,
    ironGear,
    ironPipe,
    waterPump,
    waterBoiler,
    steamGenerator,
    solarPanel,
    accumulator,
    sciencePack1,
    sciencePack2,
    sciencePack3,
    advancedCircuit,
    battery,
    processingUnit,
    speedModule1,
    productivityModule1,
    efficiencyModule1,
};

export const MANUAL_ACTIONS: Record<string, ManualActionDefinition> = {
    coal: {
        id: "manual-coal",
        resourceId: "coal",
        durationMs: 2000,
        reward: {itemId: "coal", quantity: 1},
    },
    iron: {
        id: "manual-iron",
        resourceId: "iron",
        durationMs: 2000,
        reward: {itemId: "iron", quantity: 1},
    },
    copper: {
        id: "manual-copper",
        resourceId: "copper",
        durationMs: 2000,
        reward: {itemId: "copper", quantity: 1},
    },
    stone: {
        id: "manual-stone",
        resourceId: "stone",
        durationMs: 2000,
        reward: {itemId: "stone", quantity: 1},
    },
    wood: {
        id: "manual-wood",
        resourceId: "wood",
        durationMs: 1500,
        reward: {itemId: "wood", quantity: 1},
    },
};

export const MINER_BEHAVIORS: Record<string, MinerBehavior> = {
    miner1: {
        id: "miner1",
        supportedResources: ["coal", "iron", "copper", "stone"],
        cycleTimeMs: 5000,
        outputPerCycle: 2,
        fuelCostPerCycle: {itemId: "coal", quantity: 1},
    },
    miner2: {
        id: "miner2",
        supportedResources: ["coal", "iron", "copper", "stone"],
        cycleTimeMs: 3500,
        outputPerCycle: 2,
    },
};

export const POWER_CONSTANTS = {
    waterPerPumpPerSecond: 100,
    boilerCycleMs: 5000,
    boilerWaterPerCycle: 50,
    boilerSteamPerCycle: 500,
    boilerFuelCost: {itemId: "coal", quantity: 1},
    generatorSteamPerSecond: 40,
    boilerStackSize: 5,
    generatorStackSize: 20,
};

// Energy system constants (1 coal = 1 MJ)
export const FUEL_ENERGY_VALUES: Record<string, number> = {
    coal: 1.0, // 1 MJ per coal
    wood: 0.5, // 0.5 MJ per wood (half of coal)
};

// Energy costs for mining and smelting operations
export const ENERGY_COSTS = {
    mining: 0.1, // 0.1 MJ per ore mined
    smeltingIron: 0.2, // 0.2 MJ per iron plate
    smeltingCopper: 0.2, // 0.2 MJ per copper plate
    smeltingStone: 0.4, // 0.4 MJ per stone brick
    smeltingSteel: 0.8, // 0.8 MJ per steel plate
};

export const FUEL_BUFFER_CAPACITY = 100; // Maximum fuel units a machine can hold

export const SMELTER_RECIPES: Record<string, SmelterRecipeDefinition> = {
    iron: {
        id: "smelt-iron",
        inputId: "iron",
        outputId: "ironplate",
        inputQuantity: 1,
        outputQuantity: 1,
        cycleTimeMs: 4000,
        fuelCostPerCycle: {itemId: "coal", quantity: 1},
    },
    copper: {
        id: "smelt-copper",
        inputId: "copper",
        outputId: "copperplate",
        inputQuantity: 1,
        outputQuantity: 1,
        cycleTimeMs: 4000,
        fuelCostPerCycle: {itemId: "coal", quantity: 1},
    },
    stone: {
        id: "smelt-stone-brick",
        inputId: "stone",
        outputId: "stoneBrick",
        inputQuantity: 2,
        outputQuantity: 1,
        cycleTimeMs: 4500,
        fuelCostPerCycle: {itemId: "coal", quantity: 1},
    },
    steel: {
        id: "smelt-steel",
        inputId: "ironplate",
        outputId: "steelPlate",
        inputQuantity: 5,
        outputQuantity: 1,
        cycleTimeMs: 6000,
        fuelCostPerCycle: {itemId: "coal", quantity: 1},
    },
};

const startingQuantities: Record<string, number> = {
    miner1: 1,
    smelter1: 1,
};

export const ItemList = Object.values(GameItems);

export const buildStartingInventory = () => ItemList.map(item => ({
    item,
    quantity: startingQuantities[item.id] ?? 0,
}));
